
/* eslint-disable no-console */
import Cookies from 'https://esm.archive.org/js-cookie';
import Snowflakes from 'https://esm.archive.org/magic-snowflakes@^6.0.0';

import 'https://esm.archive.org/@internetarchive/donation-form-edit-donation@^1.1.6';
import { DonationPaymentInfo } from 'https://esm.archive.org/@internetarchive/donation-form-data-models@^0.3.6';
import 'https://esm.archive.org/@internetarchive/elements@^1.1.5/ia-donation-thermometer/ia-donation-thermometer';


import $ from '../util/jquery.js';
import DonationBannerIframeHandler from '../donation-banner/donation-banner-iframe-handler.js';
import AnalyticsHandler from '../analyticsHandler/analyticsHandler.js';
import RemindForm from '../donation-banner/remind_form.js';

console?.log('donation-banner loaded', {
  version: '1.1.1',
});

class DonationBanner {
  /**
   * @param {Element} bannerElement the one rendered <header> this instance owns. PHP renders
   *   both the desktop and mobile banners; we instantiate one DonationBanner per element and
   *   wire each, so whichever viewport is showing (e.g. after a resize) is fully interactive —
   *   not just the banner that happened to be visible at page load.
   */
  constructor(bannerElement) {
    this.bannerElement = bannerElement;
  }

  addCloseSupport() {
    const bannerClose = function hideDonationBannerOnClickingXbutton(event) {
      event.stopPropagation();

      // set a cookie to make the donate banner not show for 6 weeks..
      const daysToHideBanner = 42;
      DonationBannerIframeHandler.postMessage('hide banner', daysToHideBanner); // nixxx lines below & relateds, once iframe used by IA
      this.bannerElement.style.display = 'none';
      this.logEvent('CloseClicked', true);
      this.setDonationCookie(daysToHideBanner);
    };

    const closeButton = this.bannerElement?.querySelector('#donate-close-button');
    if (closeButton)
      closeButton.addEventListener('click', bannerClose.bind(this));
  }

  // Wire a single banner's controls. Global, once-per-page concerns (iframe height handling,
  // cross-widget value sync, the Viewed event) are handled in the DOMContentLoaded bootstrap.
  setupBanner() {
    if (!this.bannerElement) return;
    this.donationInfoError = false;
    this.addCloseSupport();
    this.setupDonationForm();
    this.setupMinimalDonateButton();
    this.setupSnow();
    const remindForm = new RemindForm(
      $(this.bannerElement),
      this.setDonationCookie,
    );
    remindForm.setup();
  }

  setDonationCookie(expires) {
    DonationBanner.setDonationCookie(expires);
    DonationBannerIframeHandler.postMessage('set cookie', expires);
  }

  // The element lookups below are scoped to `this.bannerElement` (this instance's banner),
  // not `document`. PHP renders both desktop and mobile <header>s with the SAME element IDs,
  // so a document-wide query would always return the first (desktop) one — scoping to the
  // instance's own element is what keeps each banner's controls wired to itself.
  get donationFormElement() {
    return this.bannerElement?.querySelector('donation-form-edit-donation');
  }

  get minimalDonateButtonElement() {
    return this.bannerElement?.querySelector('#minimal-donate-button');
  }

  get donationOrigin() {
    const banner = this.bannerElement;
    const experimentName = banner.dataset.exp;
    const variantName = banner.dataset.variant;
    // Derive the -Desktop/-Mobile suffix from this banner's own modality class, so each banner's
    // origin is correct regardless of the current window width (the hidden banner isn't clickable).
    const responsiveMode = banner.classList.contains('js-donate-banner--mobile') ? 'Mobile' : 'Desktop';
    const origin = `DonateBanner-${experimentName}-${variantName}-${responsiveMode}`;
    console.log('PSA Donation Banner donationOrigin: ', {
      responsiveMode,
      currentWindowWidth: window.innerWidth,
      origin,
    });
    return origin;
  }

  /*
   * Sets a cookie to indicate that a donation transaction has completed.
   * @param {number} daysCookieWillExpire
   */
  static setDonationCookie(daysCookieWillExpire) {
    // using js-cookie library
    const expires = parseInt(daysCookieWillExpire, 10);
    if (!expires) return;

    const cookie = {
      path: '/',
      expires,
      domain: '.archive.org',
    };

    Cookies.set('donation', 'x', cookie);
    Cookies.set(
      'donation',
      'x',
      $.extend(cookie, { domain: '.openlibrary.org' }),
    );
  }

  setupSnow() {
    if (this.bannerElement.dataset.snowfall !== 'on') return;
    const snowflakes = new Snowflakes({
      container: this.bannerElement.querySelector('#donate-body-background-layer2'),
      count: 100,
      minOpacity: 0.08,
      maxOpacity: 0.15,
      minSize: 4,
      maxSize: 8,
      rotation: true,
      speed: 0.15,
    });
    snowflakes.start();
  }

  setupMinimalDonateButton() {
    const donateButton = this.minimalDonateButtonElement;
    if (!donateButton) return;

    donateButton.addEventListener('click', () => {
      this.logEvent('MinimalDonateButtonClicked');
      // We only want to set the donation cookie when the user clicks "Continue" if the
      // user is seeing the iframe version. We can't set a donation cookie on non-archive.org
      // domains so if they make a donation, then go back, they'll see the banner again.
      DonationBannerIframeHandler.postMessage('set cookie', 30);
      const formInfo = this.donationFormElement?.donationInfo;
      const amount = formInfo?.amount ?? this.bannerElement.dataset.defaultselectedamount;
      const frequency = formInfo?.donationType ??
        this.bannerElement.dataset.defaultselectedfrequency;
      const searchParams = new URLSearchParams();
      if (amount) searchParams.set('amt', amount);
      if (frequency) searchParams.set('contrib_type', frequency);
      const qs = searchParams.toString();
      const url = qs ? `${this.baseDonatePageUrl}&${qs}` : this.baseDonatePageUrl;
      window.top.location = url;
    });
  }

  get baseDonatePageUrl() {
    const banner = this.bannerElement;
    const baseUrl = banner.dataset.baseurl;
    const donationSourceData = banner.dataset.donationsourcedata;
    const variantDollarAmounts = banner.dataset.variantdollaramounts;
    const variantAmountLayout = banner.dataset.variantamountlayout;
    const variantFrequencyMode = banner.dataset.variantfrequencymode;
    // const defaultSelectedFrequency = banner.dataset.defaultselectedfrequency;
    const bannerTemplate = banner.dataset.bannertemplate;
    const { platform } = banner.dataset;


    const searchParams = new URLSearchParams();
    if (this.donationOrigin) {
      searchParams.set('origin', this.donationOrigin);
    }
    if (donationSourceData) {
      searchParams.set('referer', donationSourceData);
    }
    if (variantDollarAmounts) {
      searchParams.set('dollarAmounts', variantDollarAmounts);
    }
    if (variantAmountLayout) {
      searchParams.set('amountLayout', variantAmountLayout);
    }
    if (variantFrequencyMode) {
      searchParams.set('frequencyMode', variantFrequencyMode);
    }
    if (platform) {
      searchParams.set('platform', platform);
    }
    if (bannerTemplate) {
      searchParams.set('bannerTemplate', bannerTemplate);
    }
    const url = `${baseUrl}/donate?${searchParams.toString()}`;
    return url;
  }

  openPsaUrl() {
    const banner = this.bannerElement;
    const fallbackUrl = 'https://archive.org/donate';
    const destinationRaw =
      banner.dataset.psabannerdestinationurl || fallbackUrl;
    const amount = banner.dataset.defaultselectedamount;
    const frequency = banner.dataset.defaultselectedfrequency;

    // The PSA banner has no widget to click "Continue" on, so send the donate page the same
    // params the full-size banner builds (referer, dollar amounts, layout, etc.) plus the CMS
    // default amount and frequency. Params already present on the destination win, so a campaign
    // `origin` baked into the CMS destination URL is preserved rather than overwritten.
    let url = destinationRaw;
    try {
      const destination = new URL(destinationRaw, banner.dataset.baseurl);
      const params = new URL(this.baseDonatePageUrl).searchParams;
      if (amount) params.set('amt', amount);
      if (frequency) params.set('contrib_type', frequency);
      params.forEach((value, key) => {
        if (!destination.searchParams.has(key)) {
          destination.searchParams.set(key, value);
        }
      });
      url = destination.toString();
    } catch {
      // Malformed CMS destination URL: navigate there as-is, still carrying the default amount.
      if (amount) {
        url += `${destinationRaw.includes('?') ? '&' : '?'}amt=${encodeURIComponent(amount)}`;
      }
    }

    this.goToDonatePage(url, 'ContinueClicked');
  }

  setupDonationForm() {
    const editDonationForm = this.donationFormElement;

    const defaultSelectedFrequency =
      this.bannerElement.dataset.defaultselectedfrequency;
    if (defaultSelectedFrequency && editDonationForm) {
      const existingInfo = editDonationForm.donationInfo;
      editDonationForm.donationInfo = new DonationPaymentInfo({
        donationType: defaultSelectedFrequency,
        amount: existingInfo.amount,
        coverFees: existingInfo.coverFees,
      });
    }

    let donationInfo = editDonationForm?.donationInfo ?? {
      amount: 5,
      donationType: 'one-time',
      coverFees: false,
    };
    const isPsaBanner = this.bannerElement.classList.contains('formdesign-psa');
    if (isPsaBanner) {
      // make banner clickable
      this.bannerElement.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.openPsaUrl();
      });

      // make links clickable
      const ctaButtons = this.bannerElement.querySelectorAll('.psa-cta');
      console.log('PSA Donation Banner CTA: ', ctaButtons);
      if (ctaButtons.length === 0) return;
      ctaButtons.forEach((ctaButton) => {
        ctaButton.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          this.openPsaUrl();
        });
      });

      return;
    }

    editDonationForm?.addEventListener('donationInfoChanged', (event) => {
      this.donationInfoError = false;
      donationInfo = event.detail.donationInfo;

      const isMinimalBanner =
        this.bannerElement.classList.contains('formdesign-minimal');

      console?.log('donationInfoChanged', {
        isMinimalBanner,
        ...donationInfo,
      });

      // check if minimal
      if (isMinimalBanner) {
        // send patron to donate page
        const url = `${this.baseDonatePageUrl}&amt=${donationInfo.amount}&contrib_type=${donationInfo.donationType}`;
        console?.log(
          'donationInfoChanged - IN minimal banner check - post cookie',
          { url },
        );
        this.goToDonatePage(url, 'ContinueClicked');
      }
    });

    editDonationForm?.addEventListener('editDonationError', () => {
      this.donationInfoError = true;
    });

    const continueButton = this.bannerElement.querySelector('#continue-button');
    if (!continueButton) {
      return;
    }
    continueButton.addEventListener('click', () => {
      if (this.donationInfoError) return;

      this.logEvent('ContinueClicked');
      const bannerDataSet = this.bannerElement.dataset;

      const encodedSelectedTextColor = encodeURIComponent(
        bannerDataSet.selectedtextcolor,
      );

      const encodedSelectedFillColor = encodeURIComponent(
        bannerDataSet.selectedfillcolor,
      );

      const encodedDonatePageSelectedFillColor = encodeURIComponent(
        bannerDataSet.donatepageselectedfillcolor,
      );

      const encodedPageSelectedTextColor = encodeURIComponent(
        bannerDataSet.donatepageselectedtextcolor,
      );

      const encodedFormTextColor = encodeURIComponent(
        bannerDataSet.formtextcolor,
      );

      let customSelectedTextColor = bannerDataSet.selectedtextcolor
        ? `&selectedTextColor=${encodedSelectedTextColor}`
        : '';
      let customSelectedFillColor = bannerDataSet.selectedfillcolor
        ? `&selectedFillColor=${encodedSelectedFillColor}`
        : '';
      /* allow for override to custom selected option colors for donate bage */
      if (bannerDataSet.donatepageselectedfillcolor) {
        customSelectedFillColor = `&selectedFillColor=${encodedDonatePageSelectedFillColor}`;
      }
      if (bannerDataSet.donatepageselectedtextcolor) {
        customSelectedTextColor = `&selectedTextColor=${encodedPageSelectedTextColor}`;
      }

      const customFormTextColor = bannerDataSet.formtextcolor
        ? `&formTextColor=${encodedFormTextColor}`
        : '';

      // Read the widget's current value at click time so a value synced in from the other
      // viewport's widget is honored (falls back to the last donationInfoChanged snapshot).
      const info = this.donationFormElement?.donationInfo ?? donationInfo;
      const url = `${this.baseDonatePageUrl}&amt=${info.amount}&contrib_type=${info.donationType}&coverFees=${info.coverFees}${customSelectedTextColor}${customSelectedFillColor}${customFormTextColor}`;
      // We only want to set the donation cookie when the user clicks "Continue" if the
      // user is seeing the iframe version. We can't set a donation cookie on non-archive.org
      // domains so if they make a donation, then go back, they'll see the banner again.
      DonationBannerIframeHandler.postMessage('set cookie', 30);
      window.top.location = url;
    });
  }

  goToDonatePage(url, eventName) {
    this.logEvent(eventName);
    this.setDonationCookie(3);
    window.top.location = url;
  }

  /**
   * Keep the desktop and mobile donation widgets in sync.
   *
   * PHP renders both the desktop and mobile <header>s into the DOM at once; banner.css shows
   * one per viewport. Each has its own <donation-form-edit-donation> with independent state, so
   * a selection (amount / frequency / cover-fees) made before the browser crosses the 768px
   * breakpoint would otherwise be lost when the other viewport's banner becomes visible. Mirror
   * `donationInfo` between the two full-form widgets so the choice persists across a resize.
   *
   * Only full editable forms are synced — minimal banners navigate away on change and PSA banners
   * have no form. A value comparison guards against the feedback loop that would otherwise arise
   * from one form's programmatic update echoing back as another `donationInfoChanged`.
   */
  static setupViewportSync() {
    const formSelector = 'donation-form-edit-donation';
    const forms = Array.from(document.querySelectorAll(formSelector)).filter((form) => {
      const header = form.closest('.js-donate-banner');
      if (!header) return false;
      const minimal = header.classList.contains('formdesign-minimal');
      const psa = header.classList.contains('formdesign-psa');
      return !minimal && !psa;
    });
    if (forms.length < 2) return; // only one viewport has a syncable form

    const sameInfo = (a, b) => !!a &&
      !!b &&
      a.amount === b.amount &&
      a.donationType === b.donationType &&
      a.coverFees === b.coverFees;

    forms.forEach((form) => {
      form.addEventListener('donationInfoChanged', (event) => {
        const info = event.detail?.donationInfo;
        if (!info) return;
        forms.forEach((other) => {
          if (other === form) return;
          // Skip when already equal — this is what stops the propagated update from
          // bouncing back as an endless donationInfoChanged loop.
          if (sameInfo(other.donationInfo, info)) return;
          // The receiving widget reconciles its own preset/custom selection from this write
          // (requires donation-form-edit-donation >= 1.1.6 — older versions had a sticky
          // customAmountSelected that left custom selected + empty, see WEBDEV-8585).
          // eslint-disable-next-line no-param-reassign
          other.donationInfo = new DonationPaymentInfo({
            amount: info.amount,
            donationType: info.donationType,
            coverFees: info.coverFees,
          });
        });
      });
    });
  }

  logEvent(eventAction, sendAsSampledEvent = false) {
    const analyticsHandler = new AnalyticsHandler();
    const banner = this.bannerElement;
    const debugMode = banner.dataset.debugmode === 'true';
    const { platform } = banner.dataset;
    const eventName = debugMode ? 'DonateBannerDebug' : 'DonateBanner';

    // The `Viewed` event is very noisy so we want to sample the calls for IA and WB
    // because even sampled, we can still extrapolate actual values, but OL traffic
    // is too low to extrapolate values. For all other events, we want them without
    // sampling so we get accurate numbers.
    if (
      sendAsSampledEvent ||
      (eventAction === 'Viewed' && (platform === 'ia' || platform === 'wb'))
    ) {
      analyticsHandler.send_event(eventName, eventAction, this.donationOrigin);
    } else {
      analyticsHandler.send_event_no_sampling(
        eventName,
        eventAction,
        this.donationOrigin,
      );
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // PHP renders both the desktop and mobile <header>s; wire each so the user can interact with
  // whichever viewport is showing (update the amount, hit Continue, close it) — not only the
  // banner that was visible at page load.
  const banners = Array.from(document.querySelectorAll('.js-donate-banner'));
  if (banners.length === 0) return;

  const instances = banners.map((banner) => new DonationBanner(banner));
  instances.forEach((instance) => instance.setupBanner());

  // Mirror user-selected donation values (amount/frequency/coverFees) between the two widgets.
  DonationBanner.setupViewportSync();

  // Global, once-per-page: iframe height handling, and a single Viewed event for the banner the
  // user actually sees (the off-viewport banner is display:none and shouldn't count as viewed).
  DonationBannerIframeHandler.init();
  const visibleSelector = DonationBannerIframeHandler.get_visible_banner_selector();
  const visibleBanner = document.querySelector(visibleSelector);
  const visibleInstance = instances.find((instance) => instance.bannerElement === visibleBanner);
  if (visibleInstance) visibleInstance.logEvent('Viewed');
});
